import { DecimalPipe } from "@angular/common";
import { Component, Input } from "@angular/core";
import { ImageUrlDirective } from "@app/directives";

/**
 * A customer's cart exactly as they see it on the cart page, built by
 * web-backend with the customer's own cart code (GET /api/v1/get-user-cart):
 * items by merchant with each merchant's delivery line, then the order summary.
 * Nothing here recalculates a figure - it shows what the customer is shown.
 */
export interface CustomerCartView {
  iso2: string;
  currency: string;
  estimated: boolean;
  address: { state: string | null; postalCode: string | null } | null;
  merchants: Array<{
    vendorId: number;
    vendorName: string;
    items: Array<{
      id: number;
      productName: string;
      option: string | null;
      image: string | null;
      price: number;
      quantity: number;
      subtotal: number;
      outOfStock: boolean;
    }>;
    delivery: { label: string; amount: number; note: string | null };
    walkIn: { text: string; appliesTo: string[] } | null;
  }>;
  summary: {
    subtotal: number;
    shippingLabel: string;
    shipping: number;
    shippingNote: string | null;
    zoneDiscount: number;
    promo: { code: string; label: string; amount: number } | null;
    deliveryDiscounts: Array<{ text: string; active: boolean }>;
    totalLabel: string;
    total: number;
  };
  totalItems: number;
}

@Component({
  selector: "fh-customer-cart-view",
  standalone: true,
  imports: [DecimalPipe, ImageUrlDirective],
  template: `
    <p class="seen-as">
      As the customer sees it ({{ cart.iso2 }}{{ cart.address ? '' : ', no delivery address yet' }})
    </p>
    @for (m of cart.merchants; track m.vendorId) {
      <div class="merchant">
        <div class="merchant-name">{{ m.vendorName || '—' }}</div>
        @if (m.walkIn) {
          <div class="walk-in">
            <div class="walk-in-title">Walk-in Collection</div>
            <div>{{ m.walkIn.text }}</div>
            <div class="muted">Applies to: {{ m.walkIn.appliesTo.join(', ') }}</div>
          </div>
        }
        <table>
          <thead>
            <tr>
              <th>Image</th>
              <th>Product</th>
              <th>Option</th>
              <th class="num">Price</th>
              <th class="num">Qty</th>
              <th class="num">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            @for (item of m.items; track item.id) {
              <tr [class.oos]="item.outOfStock">
                <td>
                  <img fhImageUrl [originalUrl]="item.image ?? ''" [alt]="item.productName" class="prod-img" />
                </td>
                <td class="prod-name">
                  {{ item.productName }}
                  @if (item.outOfStock) {
                    <span class="badge">Out of stock</span>
                  }
                </td>
                <td class="muted">{{ item.option || '—' }}</td>
                <td class="num">{{ cart.currency }} {{ item.price | number: '1.2-2' }}</td>
                <td class="num">{{ item.quantity }}</td>
                <td class="num">{{ cart.currency }} {{ item.subtotal | number: '1.2-2' }}</td>
              </tr>
            }
          </tbody>
        </table>
        <div class="delivery">
          <span>{{ m.delivery.label }}</span>
          <span class="right">
            <strong>{{ cart.currency }} {{ m.delivery.amount | number: '1.2-2' }}</strong>
            @if (m.delivery.note) {
              <span class="muted small">{{ m.delivery.note }}</span>
            }
          </span>
        </div>
      </div>
    }

    <div class="summary">
      <div class="row"><span>Subtotal</span><span>{{ cart.currency }} {{ cart.summary.subtotal | number: '1.2-2' }}</span></div>
      <div class="row"><span>{{ cart.summary.shippingLabel }}</span><span>{{ cart.currency }} {{ cart.summary.shipping | number: '1.2-2' }}</span></div>
      @if (cart.summary.shippingNote) {
        <div class="note">{{ cart.summary.shippingNote }}</div>
      }
      @if (cart.summary.zoneDiscount > 0) {
        <div class="row good"><span>Zonal Delivery Discount</span><span>- {{ cart.currency }} {{ cart.summary.zoneDiscount | number: '1.2-2' }}</span></div>
      }
      @if (cart.summary.promo) {
        <div class="row good">
          <span>{{ cart.summary.promo.label }} ({{ cart.summary.promo.code }})</span>
          <span>- {{ cart.currency }} {{ cart.summary.promo.amount | number: '1.2-2' }}</span>
        </div>
      }
      @for (d of cart.summary.deliveryDiscounts; track $index) {
        <div class="discount" [class.good]="d.active">{{ d.active ? '●' : '○' }} {{ d.text }}</div>
      }
      <div class="row total"><span>{{ cart.summary.totalLabel }}</span><span>{{ cart.currency }} {{ cart.summary.total | number: '1.2-2' }}</span></div>
    </div>
  `,
  styles: [
    `
    :host { display: block; font-size: 13px; color: #333; }
    .seen-as { margin: 0 0 8px; color: #6b7280; font-size: 12px; }
    .merchant { border: 1px solid #eef0f3; border-radius: 8px; padding: 10px 12px; margin-bottom: 10px; }
    .merchant-name { font-weight: 700; margin-bottom: 6px; }
    .walk-in { background: #eff6ff; border: 1px solid #dbeafe; border-radius: 6px; padding: 8px 10px; margin-bottom: 8px; color: #1e40af; }
    .walk-in-title { font-weight: 600; font-size: 12px; }
    table { width: 100%; border-collapse: collapse; }
    th { text-align: left; font-weight: 600; color: #555; padding: 6px 8px; border-bottom: 1px solid #e5e7eb; background: #f5f7fa; white-space: nowrap; }
    td { padding: 6px 8px; border-bottom: 1px solid #f3f4f6; vertical-align: middle; }
    .num { text-align: right; white-space: nowrap; }
    .prod-img { width: 40px; height: 40px; object-fit: cover; border-radius: 6px; border: 1px solid #eee; }
    .prod-name { font-weight: 500; }
    .muted { color: #888; }
    .small { font-size: 11px; display: block; text-align: right; }
    .badge { margin-left: 6px; font-size: 11px; color: #b91c1c; background: #fee2e2; border-radius: 4px; padding: 1px 6px; }
    tr.oos td { opacity: 0.7; }
    .delivery { display: flex; justify-content: space-between; gap: 12px; margin-top: 8px; padding: 8px 10px; background: #f5f7fa; border-radius: 6px; }
    .right { text-align: right; }
    .summary { margin-top: 12px; padding: 12px 14px; background: #f5f7fa; border-radius: 8px; }
    .row { display: flex; justify-content: space-between; gap: 12px; padding: 3px 0; }
    .row span:last-child { white-space: nowrap; }
    .note { font-size: 11px; color: #6b7280; margin: -2px 0 4px; }
    .good { color: #16a34a; }
    .discount { font-size: 12px; color: #9ca3af; padding: 2px 0; }
    .discount.good { color: #16a34a; }
    .total { font-weight: 700; font-size: 15px; border-top: 1px solid #e5e7eb; margin-top: 6px; padding-top: 8px; color: #1155cc; }
  `,
  ],
})
export class CustomerCartViewComponent {
  @Input({ required: true }) cart!: CustomerCartView;
}
